import { Avatar, Box, Group, Select, Table, Text, ThemeIcon } from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import { CheckIcon, ImageIcon, PackageIcon, UserIcon } from "@phosphor-icons/react"
import { Fragment, useState } from "react"
import { useTranslation } from "react-i18next"
import useSWR from "swr"
import { nameOptionsFilter } from "@dehub/theme"
import { PullSheetApi } from "../../apis"
import { IDeliveryNotePlus } from "../../models/DeliveryNote"
import { IWarehouse, IWarehousePlus } from "../../models/Warehouse"
import { message } from "../../utils/message"
import { renderName } from "../../utils/render-name"
import { DeliveryActionButton } from "../delivery/action-controls"
import { useDeliveryActions } from "../delivery/delivery-actions"
import { translateBackendError } from "../delivery/translate-error"
import classes from "./table.module.css"

type Entry = { id: string; loadingDate: string | null; warehouseStaffId: string | null }

/**
 * Lines per warehouse with one column per delivery note (canvas Ord_DeliveryApprove).
 * «Зөвшөөрөх» creates the warehouse's pull sheet (POST /pull_sheet, ORD_DN_PULLSHEET_ASSIGN).
 * O99: the table stays on screen while saving; only the pressed button shows loading.
 */
export default function SupplierDeliveryDistributingTable({
  warehouses,
  deliveryNote,
  onApproved,
}: {
  warehouses: Array<IWarehouse | IWarehousePlus>
  deliveryNote: IDeliveryNotePlus[]
  onApproved: (warehouseId: string) => void
}) {
  const { t } = useTranslation(["delivery", "common"])
  const { permissionState } = useDeliveryActions()
  const approveState = permissionState("approveLoading")
  const [entries, setEntries] = useState<Entry[]>(() =>
    (warehouses as IWarehousePlus[]).map((w) => ({ id: w.id ?? "", loadingDate: null, warehouseStaffId: w.warehouseUser?.id ?? null })),
  )
  const [savingId, setSavingId] = useState<string | null>(null)
  const [today] = useState(() => new Date())

  const { data: warehouseStaff } = useSWR("/ord/pull_sheet/warehouse_staff_select", async () => {
    try {
      return await PullSheetApi.warehouseStaffSelect({ query: "", limit: 50 })
    } catch {
      return []
    }
  })

  const entryOf = (id: string) => entries.find((e) => e.id === id)
  const patch = (id: string, value: Partial<Entry>) =>
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...value } : e)))

  const onSubmit = async (warehouseId: string) => {
    const entry = entryOf(warehouseId)
    if (!entry?.loadingDate) return
    setSavingId(warehouseId)
    try {
      await PullSheetApi.create({
        warehouseId,
        loadingDate: entry.loadingDate,
        deliveryNoteIds: deliveryNote.map((dn) => dn.id),
        warehouseStaffId: entry.warehouseStaffId,
      })
      message.success(t("manage.approve.warehouseDone"))
      onApproved(warehouseId)
    } catch (err) {
      message.error(translateBackendError(err))
    } finally {
      setSavingId(null)
    }
  }

  const colCount = 7 + deliveryNote.length

  return (
    <Box className={classes.scroll}>
      <Table verticalSpacing="sm" horizontalSpacing="md" miw={960}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={48}>#</Table.Th>
            <Table.Th w={64}>{t("table.image")}</Table.Th>
            <Table.Th>{t("table.dehubCode")}</Table.Th>
            <Table.Th>{t("table.sku")}</Table.Th>
            <Table.Th>{t("table.productName")}</Table.Th>
            <Table.Th w={90}>{t("table.unit")}</Table.Th>
            <Table.Th w={80} ta="center">
              {t("table.total")}
            </Table.Th>
            {deliveryNote.map((dn) => (
              <Table.Th key={dn.id} ta="center" ff="monospace">
                {dn.refCode}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {(warehouses as IWarehousePlus[]).map((warehouse) => {
            const wh = { ...warehouse, id: warehouse.id ?? "" }
            const entry = entryOf(wh.id)
            return (
              <Fragment key={wh.id}>
                <Table.Tr className={classes.warehouseRow}>
                  <Table.Td colSpan={colCount}>
                    <Group gap="lg" wrap="wrap" align="center">
                      <Group gap="sm" wrap="nowrap">
                        <ThemeIcon size={36} radius="md" variant="light" color="gray">
                          <PackageIcon size={18} />
                        </ThemeIcon>
                        <Box>
                          <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                            {t("table.warehouse")}
                          </Text>
                          <Text fw={650} fz="var(--nw-fz-body)">
                            {wh.name ?? "-"}
                          </Text>
                        </Box>
                      </Group>
                      <Group gap="sm" wrap="nowrap">
                        <ThemeIcon size={36} radius="md" variant="light" color="gray">
                          <UserIcon size={18} />
                        </ThemeIcon>
                        <Select
                          label={t("table.warehouseUser")}
                          placeholder={t("common:select")}
                          value={entry?.warehouseStaffId ?? null}
                          onChange={(v) => patch(wh.id, { warehouseStaffId: v })}
                          data={(warehouseStaff || []).map((u: { id: string }) => ({ value: u.id, label: renderName(u) }))}
                          searchable
                          filter={nameOptionsFilter}
                          clearable
                          w={220}
                        />
                      </Group>
                      <Group gap="sm" wrap="nowrap" ml="auto" align="flex-end">
                        <DatePickerInput
                          aria-label={t("table.selectDeliveryDate")}
                          placeholder={t("manage.approve.pickDate")}
                          value={entry?.loadingDate ?? null}
                          onChange={(v) => patch(wh.id, { loadingDate: v ? String(v) : null })}
                          minDate={today}
                          valueFormat="YYYY-MM-DD"
                          w={190}
                        />
                        <DeliveryActionButton
                          state={approveState}
                          disabledReason={!entry?.loadingDate ? t("table.selectDeliveryDate") : undefined}
                          color="dark"
                          leftSection={<CheckIcon size={16} />}
                          loading={savingId === wh.id}
                          onClick={() => onSubmit(wh.id)}
                        >
                          {t("manage.approve.approveWarehouse")}
                        </DeliveryActionButton>
                      </Group>
                    </Group>
                  </Table.Td>
                </Table.Tr>

                {(wh.lines || []).map((line, index) => (
                  <Table.Tr key={`${wh.id}${line.id}`}>
                    <Table.Td>
                      <Text ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text-2)">
                        {String(index + 1).padStart(2, "0")}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Avatar size={36} src={line.image || null} radius="sm">
                        <ImageIcon size={16} />
                      </Avatar>
                    </Table.Td>
                    <Table.Td>
                      <Text ff="monospace" fw={650} fz="var(--nw-fz-mono)">
                        {line.refCode ?? "-"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text ff="monospace" fz="var(--nw-fz-mono)">
                        {line.skuCode ?? "-"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="var(--nw-fz-body)" lineClamp={2}>
                        {line.name ?? "-"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="var(--nw-fz-body)">{line.unit ?? "-"}</Text>
                    </Table.Td>
                    <Table.Td ta="center">
                      <Text ff="monospace" fw={650} fz="var(--nw-fz-mono)">
                        {(line as { baseUnitQuantity?: number }).baseUnitQuantity ?? 0}
                      </Text>
                    </Table.Td>
                    {deliveryNote.map((dn) => {
                      const dnLine = (dn.lines || []).find((c: { variantId?: string }) => c.variantId === line.variantId) as
                        | { quantity?: number }
                        | undefined
                      return (
                        <Table.Td key={dn.id} ta="center">
                          <Text ff="monospace" fz="var(--nw-fz-mono)" fw={dnLine?.quantity ? 650 : 400}>
                            {dnLine?.quantity || "-"}
                          </Text>
                        </Table.Td>
                      )
                    })}
                  </Table.Tr>
                ))}
              </Fragment>
            )
          })}
        </Table.Tbody>
      </Table>
    </Box>
  )
}
